import { GridBg } from "@/svgs/grid-bg"
import { RichText, RichTextValue } from "@/components/rich-text"
import { motion } from "motion/react"
import { SanityImageAsset, SanityReference } from "sanity-codegen"
import SanityImageField from "@/integrations/sanity/components/SanityImageField"
import { SlideOnView } from "@/components/slide-on-view"

interface CaseStudyContentProps {
  content: {
    title: RichTextValue
    secondBoxLabel: string
    secondBoxValue: string
    thirdBoxLabel: string
    thirdBoxValue: string
    caseStudy: {
      description: RichTextValue
      cover: { asset: SanityReference<SanityImageAsset> }
      skills?: {
        items: {
          icon: { asset: SanityReference<SanityImageAsset> }
          title: RichTextValue
        }[]
      }
      images: {
        image: {
          asset: SanityReference<SanityImageAsset>
        }
      }[]
    }
  }
}

export function CaseStudyContent({ content }: CaseStudyContentProps) {
  return (
    <div>
      <GridBg className="absolute inset-0 -z-10 h-full max-h-[90rem] w-full" />
      <div className="mx-auto mt-15 max-w-[166rem] overflow-hidden px-6 pb-10 md:px-16 lg:px-20 xl:px-35">
        <div className="overflow-hidden">
          <SlideOnView>
            <RichText
              className="text-primary-800"
              billboardClassName="text-viola-500"
              value={content.title}
            />
          </SlideOnView>
          <div className="mt-12 grid auto-rows-fr grid-cols-1 items-center gap-y-4 md:grid-cols-3 md:grid-rows-2 md:gap-y-2">
            {content.caseStudy.skills && (
              <>
                <AnimatedBox
                  delay={0.3}
                  className="text-lg font-light text-grey-100 xl:text-xlg"
                >
                  Skills
                </AnimatedBox>
                <AnimatedBox
                  delay={0.3}
                  className="flex gap-6 font-bold text-neutral-dark md:row-start-2"
                >
                  {content.caseStudy.skills?.items.map((skill, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-1 grayscale-100"
                    >
                      <SanityImageField
                        width={32}
                        height={32}
                        asset={skill.icon.asset}
                        className="rounded-full"
                      />
                      <RichText value={skill.title} />
                    </div>
                  ))}
                </AnimatedBox>
              </>
            )}
            {content.secondBoxLabel && (
              <AnimatedBox
                delay={0.6}
                className="text-lg font-light text-grey-100 md:pl-24 xl:text-xlg"
              >
                {content.secondBoxLabel}
              </AnimatedBox>
            )}
            {content.secondBoxValue && (
              <AnimatedBox
                delay={0.6}
                className="font-bold text-neutral-dark md:row-start-2 md:pl-24"
              >
                {content.secondBoxValue}
              </AnimatedBox>
            )}
            {content.thirdBoxLabel && (
              <AnimatedBox
                delay={0.9}
                className="text-lg font-light text-grey-100 xl:text-xlg"
              >
                {content.thirdBoxLabel}
              </AnimatedBox>
            )}
            {content.thirdBoxValue && (
              <AnimatedBox delay={0.9} className="font-bold text-neutral-dark">
                {content.thirdBoxValue}
              </AnimatedBox>
            )}
          </div>
        </div>
      </div>
      <AnimatedBox delay={1.2}>
        <SanityImageField
          asset={content.caseStudy.cover.asset}
          className="h-auto w-full object-cover"
        />
      </AnimatedBox>
      <div className="relative bg-neutral-light px-5 py-10 xl:py-30">
        <GridBg className="absolute top-0 left-0 z-0 h-full w-full" />
        <RichText
          value={content.caseStudy.description}
          className="relative z-10 mx-auto max-w-[72rem] px-5"
        />
        <div className="relative z-10 mt-5 flex w-full flex-wrap justify-center gap-4 md:mt-20">
          {content.caseStudy.images?.map((image, i) => (
            <SanityImageField
              key={i}
              asset={image.image.asset}
              className="max-w-[50rem] flex-1 object-cover transition-transform duration-300 hover:scale-110"
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function AnimatedBox({
  children,
  delay,
  className,
}: {
  children: React.ReactNode
  delay: number
  className?: string
}) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        scale: 1.5,
      }}
      whileInView={{
        opacity: 1,
        scale: 1,
      }}
      transition={{ type: "spring", duration: 0.3, delay }}
      viewport={{ once: true }}
      className={className}
    >
      {children}
    </motion.div>
  )
}
